
import { useState, useMemo } from "react";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { LoadingState, EmptyState, ErrorState, PageHeader } from "@/components/features/states";
import {
  Store as StoreIcon,
  Star,
  MapPin,
  Plus,
  Minus,
  ShoppingCart,
  Trash2,
  ArrowLeft,
  CheckCircle2,
  Package,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { currencySymbol } from "@/lib/format";
import type { Product, CartItem } from "@/lib/types";

export function StoreDetailView({ storeId }: { storeId: string }) {
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const [cart, setCart] = useState<Record<string, number>>({});
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [dropoffNotes, setDropoffNotes] = useState("");
  const [orderPlaced, setOrderPlaced] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["store", storeId],
    queryFn: () => api.stores.get(storeId),
    enabled: !!storeId,
  });
  const store = data?.data?.store;

  // Group products by category.
  const grouped = useMemo(() => {
    if (!store?.products) return [];
    const map: Record<string, Product[]> = {};
    for (const p of store.products) {
      const cat = p.category || t("stores.menu");
      if (!map[cat]) map[cat] = [];
      map[cat].push(p);
    }
    return Object.entries(map);
  }, [store, t]);

  const cartItems: CartItem[] = useMemo(() => {
    if (!store?.products) return [];
    return store.products
      .filter((p) => cart[p.id] > 0)
      .map((p) => ({ product: p, qty: cart[p.id] }));
  }, [cart, store]);

  const cartCount = cartItems.reduce((n, i) => n + i.qty, 0);
  const subtotal = cartItems.reduce(
    (s, i) => s + (i.product.discountedPrice != null ? i.product.discountedPrice : i.product.price) * i.qty,
    0
  );

  const placeOrder = useMutation({
    mutationFn: () =>
      api.stores.placeOrder({
        storeId,
        items: cartItems.map((i) => ({ productId: i.product.id, qty: i.qty })),
        dropoffAddress,
        contactPhone: contactPhone || undefined,
        dropoffNotes: dropoffNotes || undefined,
      }),
    onSuccess: (res) => {
      if (res.ok && res.data) {
        setOrderPlaced(res.data.job.code);
        setCart({});
        setCheckoutOpen(false);
        setCartOpen(false);
        toast.success(t("stores.orderPlaced"));
      } else {
        toast.error(res.error || t("stores.placeOrderFailed"));
      }
    },
    onError: () => toast.error(t("errors.network")),
  });

  if (isLoading) return <LoadingState />;
  if (error || !store) return <ErrorState message={t("stores.storeNotFound")} onRetry={() => navigate("app/stores")} />;

  // Order-placed success screen.
  if (orderPlaced) {
    return (
      <div className="space-y-4">
        <Card className="border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/30">
          <CardContent className="flex flex-col items-center gap-3 p-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="text-xl font-bold text-emerald-900 dark:text-emerald-100">
              {t("stores.orderPlaced")}
            </h2>
            <p className="text-sm text-emerald-700 dark:text-emerald-300">
              {t("stores.orderPlacedDesc")}
            </p>
            <Badge variant="secondary" className="text-base font-bold">
              {orderPlaced}
            </Badge>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate("app/stores")}>
                {t("stores.title")}
              </Button>
              <Button onClick={() => navigate("app/jobs")}>
                {t("dashboard.myJobs")} <ArrowLeft className="ml-1 h-4 w-4 rotate-180" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const addToCart = (productId: string) =>
    setCart((c) => ({ ...c, [productId]: (c[productId] || 0) + 1 }));
  const removeFromCart = (productId: string) =>
    setCart((c) => {
      const next = { ...c };
      if (next[productId] > 1) next[productId]--;
      else delete next[productId];
      return next;
    });

  return (
    <div className="space-y-4 pb-24">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate("app/stores")} className="-ml-2">
        <ArrowLeft className="mr-1 h-4 w-4" /> {t("common.back")}
      </Button>

      {/* Store header */}
      <Card className="overflow-hidden">
        <div className="relative h-32 w-full bg-muted sm:h-40">
          {store.imageUrl ? (
            <img src={store.imageUrl} alt={store.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <StoreIcon className="h-12 w-12" />
            </div>
          )}
          {store.featured && (
            <Badge className="absolute left-3 top-3 bg-amber-500 text-white hover:bg-amber-500">
              <Star className="mr-0.5 h-3 w-3" /> {t("stores.featured")}
            </Badge>
          )}
        </div>
        <CardContent className="p-4">
          <h1 className="text-xl font-bold">{store.name}</h1>
          {store.description && (
            <p className="mt-1 text-sm text-muted-foreground">{store.description}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
            <span className="flex items-center gap-0.5 font-semibold text-amber-600">
              <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
              {store.rating.toFixed(1)}
              <span className="font-normal text-muted-foreground">({store.ratingCount})</span>
            </span>
            {store.address && (
              <span className="flex items-center gap-0.5 text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" /> {store.address}
              </span>
            )}
            <Badge variant="outline" className="capitalize">
              {t(`stores.${store.category}`)}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Menu grouped by category */}
      {grouped.length === 0 ? (
        <EmptyState
          icon={<Package className="h-8 w-8" />}
          title={t("stores.menu")}
          description={t("stores.noProductsDesc")}
        />
      ) : (
        grouped.map(([category, products]) => (
          <div key={category}>
            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">
              {category}
            </h2>
            <div className="space-y-2">
              {products.map((product) => {
                const qty = cart[product.id] || 0;
                const price = product.discountedPrice != null ? product.discountedPrice : product.price;
                return (
                  <Card key={product.id} className={product.available ? "" : "opacity-60"}>
                    <CardContent className="flex gap-3 p-3">
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                            <Package className="h-6 w-6" />
                          </div>
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="line-clamp-1 text-sm font-semibold">{product.name}</p>
                            {product.description && (
                              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                                {product.description}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {product.discountedPrice != null && (
                              <p className="text-[10px] text-muted-foreground line-through">
                                {currencySymbol()} {product.price.toFixed(2)}
                              </p>
                            )}
                            <p className="text-sm font-bold text-primary">
                              {currencySymbol()} {price.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <div className="mt-auto flex items-center justify-end pt-2">
                          {product.available ? (
                            qty > 0 ? (
                              <div className="flex items-center gap-2">
                                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => removeFromCart(product.id)}>
                                  <Minus className="h-3 w-3" />
                                </Button>
                                <span className="w-5 text-center text-sm font-bold">{qty}</span>
                                <Button size="icon" className="h-7 w-7" onClick={() => addToCart(product.id)}>
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => addToCart(product.id)}>
                                <Plus className="mr-1 h-3.5 w-3.5" /> {t("stores.addToCart")}
                              </Button>
                            )
                          ) : (
                            <Badge variant="secondary">{t("stores.unavailable")}</Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Sticky cart bar */}
      {cartCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border/60 bg-background/95 backdrop-blur-lg md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div className="mx-auto flex max-w-5xl items-center gap-3 p-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <ShoppingCart className="h-5 w-5" />
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold">
                {cartCount}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">{cartCount} items</p>
              <p className="text-sm font-bold">{currencySymbol()} {subtotal.toFixed(2)}</p>
            </div>
            <Button onClick={() => setCartOpen(true)} size="sm">
              {t("stores.cart")} <ArrowLeft className="ml-1 h-4 w-4 rotate-180" />
            </Button>
          </div>
        </div>
      )}

      {/* Cart sheet (desktop: side panel; mobile: bottom sheet) */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="bottom" className="mx-auto max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-primary" />
              {t("stores.cart")}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 max-h-[50vh] space-y-2 overflow-y-auto">
            {cartItems.length === 0 ? (
              <EmptyState
                icon={<ShoppingCart className="h-8 w-8" />}
                title={t("stores.cartEmpty")}
                description={t("stores.cartEmptyDesc")}
              />
            ) : (
              cartItems.map((item) => {
                const price = item.product.discountedPrice != null ? item.product.discountedPrice : item.product.price;
                return (
                  <div key={item.product.id} className="flex items-center gap-3 rounded-lg border p-2">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-muted">
                      {item.product.imageUrl ? (
                        <img src={item.product.imageUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center"><Package className="h-5 w-5 text-muted-foreground" /></div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-sm font-semibold">{item.product.name}</p>
                      <p className="text-xs text-muted-foreground">{currencySymbol()} {price.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => removeFromCart(item.product.id)}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-5 text-center text-sm font-bold">{item.qty}</span>
                      <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => addToCart(item.product.id)}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => setCart((c) => { const n = { ...c }; delete n[item.product.id]; return n; })}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                );
              })
            )}
          </div>
          {cartItems.length > 0 && (
            <div className="mt-4 space-y-3 border-t pt-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("stores.subtotal")}</span>
                <span className="text-lg font-bold">{currencySymbol()} {subtotal.toFixed(2)}</span>
              </div>
              <Button className="w-full" onClick={() => { setCartOpen(false); setCheckoutOpen(true); }}>
                {t("stores.checkout")}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Checkout sheet */}
      <Sheet open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <SheetContent side="bottom" className="mx-auto max-w-md">
          <SheetHeader>
            <SheetTitle>{t("stores.checkout")}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="addr">{t("stores.deliveryAddress")} *</Label>
              <Textarea
                id="addr"
                value={dropoffAddress}
                onChange={(e) => setDropoffAddress(e.target.value)}
                placeholder={t("stores.deliveryAddressPlaceholder")}
                rows={2}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">{t("stores.contactPhone")}</Label>
              <Input
                id="phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+253 ..."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notes">{t("stores.notes")}</Label>
              <Textarea
                id="notes"
                value={dropoffNotes}
                onChange={(e) => setDropoffNotes(e.target.value)}
                rows={2}
              />
            </div>
            <div className="rounded-lg bg-muted p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("stores.subtotal")}</span>
                <span className="font-semibold">{currencySymbol()} {subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t("stores.deliveryFee")}</span>
                <span>{t("quote.calculateQuote")}</span>
              </div>
              <div className="mt-1 border-t pt-1 text-base font-bold">
                {t("stores.placeOrder")}
              </div>
            </div>
            <Button
              className="w-full"
              size="lg"
              disabled={!dropoffAddress || placeOrder.isPending}
              onClick={() => placeOrder.mutate()}
            >
              {placeOrder.isPending
                ? t("common.loading")
                : `${t("stores.placeOrder")} — ${currencySymbol()} ${subtotal.toFixed(2)}+`}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
